import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Card, CardContent, Divider, Stack, Typography, styled } from "~/common/mui";
import type { CartSimulationPreviewData } from "~/domains/cart/types";

export interface AddPreviewData {
  products: AddPreviewDataProduct[];
}

export interface AddPreviewDataProduct {
  designation: string;
  lineItems: AddPreviewDataProductLineItem[];
}

export interface AddPreviewDataProductLineItem {
  packSize: number;
  packCode: string;
  quantity: number;
}

export interface ProductAddPreviewProps {
  data: AddPreviewData;
}

const Root = styled(Card)({
  border: "none",
});

const ProductSection = styled(CardContent)(({ theme }) => ({
  backgroundColor: theme.palette.surfaceContainer.low,
  "&:last-child": {
    paddingBottom: theme.spacing(2),
  },
}));

export const ProductAddPreview = ({ data }: ProductAddPreviewProps) => {
  const { t } = useTranslation();

  return (
    <Root>
      <Alert
        severity="info"
        sx={{ border: "none", borderBottomRightRadius: 0, borderBottomLeftRadius: 0, maxWidth: "unset" }}
      >
        {t("product.previewAdd.info", "You will see the following products in your cart.")}
      </Alert>

      {data.products.map((product, productIndex) => (
        <Fragment key={`${product.designation}-${productIndex}`}>
          <Divider sx={{ borderColor: "transparent" }} />
          <ProductSection>
            <Stack gap={0.5}>
              <Typography variant="titleMedium" fontWeight={"bold"}>
                {product.designation}
              </Typography>
              {product.lineItems.map((lineItem, lineItemIndex) => (
                <Stack
                  key={`${product.designation}-${lineItem.packCode}-${lineItemIndex}`}
                  direction="row"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Typography variant="bodyMedium" color="text.secondary">
                    {t("addToCartPopup.packSize", { amount: lineItem.packSize, code: lineItem.packCode })}
                  </Typography>
                  <Stack direction="row" gap={1}>
                    <Typography color="text.label">{t("product.previewAdd.quantity", "Quantity:")}</Typography>
                    <Typography>{lineItem.quantity}</Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </ProductSection>
        </Fragment>
      ))}
    </Root>
  );
};

ProductAddPreview.isVisible = (
  designation: string,
  packCode: string,
  previewData: CartSimulationPreviewData | null,
): previewData is CartSimulationPreviewData => {
  const totalPreviewItems = previewData?.products.reduce((sum, p) => sum + p.lineItems.length, 0) ?? 0;
  return (
    totalPreviewItems > 1 ||
    (totalPreviewItems === 1 &&
      (previewData!.products[0].designation !== designation ||
        previewData!.products[0].lineItems[0].packCode !== packCode))
  );
};
